import { useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  Select,
  Button,
  Group,
  Alert,
  Paper,
  Checkbox,
} from '@mantine/core';
import {
  IconUpload,
  IconAlertTriangle,
  IconDeviceDesktop,
  IconPhoto,
} from '@tabler/icons-react';
import { useCaptureImage, useFogImages, type FogHost } from '@/hooks';

interface CaptureImageModalProps {
  opened: boolean;
  onClose: () => void;
  host: FogHost | null;
}

export function CaptureImageModal({ opened, onClose, host }: CaptureImageModalProps) {
  const { data: imagesData } = useFogImages();
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const captureMutation = useCaptureImage();

  const images = imagesData?.images || [];

  const handleClose = () => {
    setSelectedImageId(null);
    setConfirmed(false);
    onClose();
  };

  const handleCapture = async () => {
    if (!host || !selectedImageId) return;

    try {
      await captureMutation.mutateAsync({
        hostId: host.id,
        imageId: parseInt(selectedImageId, 10),
      });
      handleClose();
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const selectedImage = images.find((img) => img.id.toString() === selectedImageId);

  const imageOptions = images
    .filter((img) => img.isEnabled !== false)
    .map((img) => ({
      value: img.id.toString(),
      label: `${img.name}${img.osName ? ` (${img.osName})` : ''}`,
    }));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Capture Image"
      size="md"
    >
      <Stack gap="md">
        {/* Host Info */}
        <Paper withBorder p="sm" radius="md" bg="gray.0">
          <Group gap="xs">
            <IconDeviceDesktop size={18} />
            <Text fw={500}>Source Host:</Text>
            <Text>{host?.name}</Text>
          </Group>
          <Text size="sm" c="dimmed" mt={4}>
            MAC: {host?.mac || host?.macs?.[0] || 'Unknown'}
          </Text>
        </Paper>

        {/* Info */}
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="blue"
          variant="light"
        >
          <Text size="sm">
            Capturing an image will create a snapshot of the host's disk.
            The host must be <strong>powered off</strong> or will be rebooted into FOG capture mode.
          </Text>
        </Alert>

        {/* Image Selection - capture to existing image */}
        <Select
          label="Capture to Image"
          placeholder="Select an existing image to overwrite"
          data={imageOptions}
          value={selectedImageId}
          onChange={setSelectedImageId}
          searchable
          clearable
          leftSection={<IconPhoto size={16} />}
          description="Select an existing image definition to capture to"
        />

        {/* Selected Image Info */}
        {selectedImage && (
          <Paper withBorder p="sm" radius="md">
            <Stack gap={4}>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Image Name</Text>
                <Text size="sm" fw={500}>{selectedImage.name}</Text>
              </Group>
              {selectedImage.osName && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Operating System</Text>
                  <Text size="sm">{selectedImage.osName}</Text>
                </Group>
              )}
              {selectedImage.path && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Path</Text>
                  <Text size="sm" ff="monospace">{selectedImage.path}</Text>
                </Group>
              )}
            </Stack>
          </Paper>
        )}

        {/* Warning for overwriting */}
        {selectedImage && (
          <Alert color="orange" variant="light">
            <Text size="sm">
              This will <strong>overwrite</strong> the existing image data for "{selectedImage.name}".
            </Text>
          </Alert>
        )}

        {/* Confirmation Checkbox */}
        <Checkbox
          label="I understand the host will reboot into capture mode"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.currentTarget.checked)}
        />

        {/* Actions */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconUpload size={16} />}
            onClick={handleCapture}
            loading={captureMutation.isPending}
            disabled={!selectedImageId || !confirmed}
            color="violet"
          >
            Start Capture
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
